import { useTranslation } from 'react-i18next'
import { useRef } from 'react'
import { Loader2, ArrowUpNarrowWide, ArrowDownWideNarrow } from 'lucide-react'

interface SortControlsProps {
  sortBy: string
  sortDirection: 'asc' | 'desc'
  onSortChange: (field: string) => void
  onDirectionToggle: () => void
  loadingSortField: boolean
  loadingDirection: boolean
}

interface SortOption {
  value: string
  label: string
}

export default function SortControls({
  sortBy,
  sortDirection,
  onSortChange,
  onDirectionToggle,
  loadingSortField,
  loadingDirection
}: SortControlsProps) {
  const { t } = useTranslation()
  const triggerRef = useRef<HTMLDivElement>(null)

  const sortOptions: SortOption[] = [
    { value: 'name', label: t('fileExplorer.sortFields.name') },
    { value: 'type', label: t('fileExplorer.sortFields.type') },
    { value: 'size', label: t('fileExplorer.sortFields.size') },
    { value: 'created_at', label: t('fileExplorer.sortFields.created') },
    { value: 'updated_at', label: t('fileExplorer.sortFields.modified') }
  ]

  const currentSort = sortOptions.find(option => option.value === sortBy) || sortOptions[0]

  const handleSortChange = (value: string) => {
    onSortChange(value)
    const elem = document.activeElement as HTMLElement
    elem?.blur()
  }

  return (
    <div className="join">
      <div className="dropdown">
        <div ref={triggerRef} tabIndex={0} role="button" className="btn join-item" aria-disabled={loadingSortField} data-testid="sort-dropdown">
          <span className="flex items-center gap-2">
            {currentSort.label}
            {loadingSortField && <Loader2 className="w-3 h-3 animate-spin" />}
          </span>
        </div>
        <ul tabIndex={-1} className="menu menu-sm dropdown-content bg-base-100 rounded-box z-[1] mt-3 w-40 p-0 shadow">
          {sortOptions.map((option) => (
            <li key={option.value}>
              <a
                onClick={() => !loadingSortField && handleSortChange(option.value)}
                className={`${sortBy === option.value ? 'active' : ''} ${loadingSortField ? 'pointer-events-none opacity-50' : ''}`}
              >
                <span className="flex-1">{option.label}</span>
                {sortBy === option.value && (
                  <span className="text-success">✓</span>
                )}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <button
        className="btn join-item"
        onClick={onDirectionToggle}
        disabled={loadingDirection}
        title={t(`fileExplorer.sortDirection.${sortDirection}`)}
        data-testid="sort-direction-toggle"
      >
        {loadingDirection ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          sortDirection === 'asc' ? <ArrowUpNarrowWide className="w-4 h-4" /> : <ArrowDownWideNarrow className="w-4 h-4" />
        )}
      </button>
    </div>
  )
}
